// src/components/DiligenceSection.jsx
// Phase B MVP: Admin-side Due Diligence checklist.
//
// Displays deal_dd_items grouped by section, filterable by assignee.
// Admins can seed from templates, edit item status/assignee/comments, and
// see attached files. Sending requests to third parties and portal-side
// upload matching are deferred to Phase C.

import React, { useState, useMemo, useEffect, useRef } from 'react';
import JSZip from 'jszip';
import {
  ChevronDown, ChevronRight, ClipboardList, Plus, X, Loader2,
  MessageSquare, FileText, Check, Trash2, Pencil, Save, Send, Mail,
  Sparkles, Link2, Upload, Download, FolderDown, Bell,
} from 'lucide-react';
import { useDdTemplates, useDealDdItems } from '../hooks/useSupabase';
import { useDisplayColors } from '../lib/ThemeContext';
import {
  generateSellerToken, generateScopedSellerToken, buildSellerPortalUrl,
  sendEmail, relinkDdFile, supabase, uploadToSharePoint, getFileUrl,
} from '../lib/supabase';
import { extractPdfExtraFields, extractXlsxExtraFields } from '../lib/pdfExtract';

// A file either lives in Supabase Storage (normal upload path — get a
// signed URL) or, for files pulled in by the SharePoint 2-way sync scan
// rather than uploaded through the app, storage_path is a sharepoint://
// reference with no real Storage object behind it — use sharepoint_url
// directly for those.
async function resolveFileUrl(f) {
  if (f.storage_path?.startsWith('sharepoint://')) {
    if (!f.sharepoint_url) throw new Error('No SharePoint link on file');
    return f.sharepoint_url;
  }
  return await getFileUrl(f.storage_path);
}

const ASSIGNEES = [
  { id: 'all',        label: 'All' },
  { id: 'seller',     label: 'Seller' },
  { id: 'counsel',    label: 'Counsel' },
  { id: 'accountant', label: 'Accountant' },
  { id: 'broker',     label: 'Broker' },
];

const STATUSES = [
  { id: 'not_started',  label: 'Not Started',  color: 'bg-gray-100 text-gray-600' },
  { id: 'requested',    label: 'Requested',    color: 'bg-yellow-100 text-yellow-700' },
  { id: 'received',     label: 'Received',     color: 'bg-purple-100 text-purple-700' },
  { id: 'under_review', label: 'Under Review', color: 'bg-orange-100 text-orange-700' },
  { id: 'complete',     label: 'Complete',     color: 'bg-green-100 text-green-700' },
  { id: 'n_a',          label: 'N/A',          color: 'bg-gray-50 text-gray-400' },
];

const PRIORITY_STYLES = {
  high:   { bg: 'bg-red-50',  text: 'text-red-700',  label: 'High' },
  medium: { bg: 'bg-blue-50', text: 'text-blue-700', label: 'Med' },
  low:    { bg: 'bg-gray-50', text: 'text-gray-500', label: 'Low' },
};

const statusMeta = (id) => STATUSES.find(s => s.id === id) || STATUSES[0];

export default function DiligenceSection({
  dealId, organizationId, primaryColor, isViewer, isAdmin, showToast,
  // Phase C: contact info + user profile, so the Send DD request flow can
  // scope the portal token and address the email. Shape:
  //   contacts = { seller: {name, email}, counsel: {name, email},
  //                accountant: {name, email}, broker: {name, email} }
  contacts, dealName, userProfile,
  // Phase C.2 — external trigger for the Send DD Request modal (from the
  // milestone timeline's Kickoff buttons). When set, open the modal with
  // that scope and immediately clear the parent's trigger state.
  pendingSendScope, onConsumePendingSendScope,
  // deal_advisor is isViewer=true everywhere else in DealView (no
  // financials/contacts/business-info), but here they need to actually
  // work the checklist — status changes (incl. N/A), a resolution note,
  // and uploading files against an item. canAct below opens exactly
  // those three controls for them while canEdit (reassign, delete,
  // internal notes, template seeding) stays admin/member-only.
  isDealAdvisor,
}) {
  const { items, loading, seedFromTemplates, updateItem, deleteItem, uploadDdItemFile } = useDealDdItems(dealId, organizationId);
  const [expanded, setExpanded] = useState(false);
  const [showSeedModal, setShowSeedModal] = useState(false);
  const [assigneeFilter, setAssigneeFilter] = useState('all');
  const [expandedItemId, setExpandedItemId] = useState(null);
  const [showSendModal, setShowSendModal] = useState(null); // scope id or null
  const { displayPrimary } = useDisplayColors(primaryColor, null);
  const [dragActive, setDragActive] = useState(false);
  const [genericUploading, setGenericUploading] = useState(false);
  const genericFileInputRef = useRef(null);
  const [zipping, setZipping] = useState(false);
  const [allDealFiles, setAllDealFiles] = useState([]);
  const [selectedFileIds, setSelectedFileIds] = useState(new Set());
  const [zippingSelected, setZippingSelected] = useState(false);
  const [notifyOnUpload, setNotifyOnUpload] = useState(false);
  const [notifyLoading, setNotifyLoading] = useState(false);

  // Upload-notification opt-in — deal_advisor has no Documents tab and
  // this Diligence panel is their whole view of the deal, so this is
  // where they'd actually look for it (a separate toggle buried in the
  // portfolio list, one screen away, wasn't being found).
  useEffect(() => {
    if (!dealId || !isDealAdvisor || !userProfile?.id) return;
    supabase
      .from('deal_access')
      .select('notify_on_upload')
      .eq('deal_id', dealId)
      .eq('user_id', userProfile.id)
      .maybeSingle()
      .then(({ data }) => setNotifyOnUpload(!!data?.notify_on_upload));
  }, [dealId, isDealAdvisor, userProfile?.id]);

  const toggleNotifyOnUpload = async () => {
    setNotifyLoading(true);
    try {
      const next = !notifyOnUpload;
      const { error } = await supabase.rpc('set_own_deal_notification_pref', { p_deal_id: dealId, p_notify: next });
      if (error) throw error;
      setNotifyOnUpload(next);
      showToast?.(next ? 'You\'ll be emailed when files are uploaded to this deal' : 'Upload notifications turned off for this deal');
    } catch (e) {
      showToast?.(e.message, 'error');
    } finally {
      setNotifyLoading(false);
    }
  };

  // "All files" — deal_advisor has no Documents tab, so this is their only
  // way to see every file on the deal (matched to a checklist item or
  // not), same as internal users get via FilesView. Includes uploader
  // name/type and upload date for an audit trail.
  useEffect(() => {
    if (!dealId) return;
    const fetchAllFiles = async () => {
      const { data, error } = await supabase
        .from('files')
        .select('id, original_name, storage_path, size_bytes, created_at, uploaded_by_name, uploaded_by_type, sharepoint_url, dd_item_id')
        .eq('deal_id', dealId)
        .order('created_at', { ascending: false });
      if (!error) setAllDealFiles(data || []);
    };
    fetchAllFiles();
    const channel = supabase
      .channel(`dd-all-files-${dealId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'files', filter: `deal_id=eq.${dealId}` }, fetchAllFiles)
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [dealId]);

  const toggleFileSelected = (id) => {
    setSelectedFileIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Andy (Cohen & Co, deal_advisor): with target companies rarely naming
  // files anything identifiable, a flat "All files" list is hard to
  // orient in. Group by the checklist item (section + item number) each
  // file is matched to, in the same order the checklist above shows
  // them, with an "Unmatched" group last for files with no dd_item_id.
  const [groupFilesByRequest, setGroupFilesByRequest] = useState(true);
  const fileGroups = useMemo(() => {
    const itemById = {};
    items.forEach(i => { itemById[i.id] = i; });
    const byItem = {};
    const unmatched = [];
    allDealFiles.forEach(f => {
      if (f.dd_item_id && itemById[f.dd_item_id]) {
        (byItem[f.dd_item_id] ||= []).push(f);
      } else {
        unmatched.push(f);
      }
    });
    const groups = Object.entries(byItem)
      .map(([itemId, files]) => ({
        key: itemId,
        label: `${itemById[itemId].section || ''} · #${itemById[itemId].item_number ?? '?'}`,
        detail: itemById[itemId].request_text,
        sortKey: [itemById[itemId].section_sort ?? 0, itemById[itemId].item_number ?? 0],
        files,
      }))
      .sort((a, b) => a.sortKey[0] - b.sortKey[0] || a.sortKey[1] - b.sortKey[1]);
    if (unmatched.length > 0) {
      groups.push({ key: 'unmatched', label: 'Unmatched', detail: 'Not attached to any checklist item', sortKey: [Infinity, Infinity], files: unmatched });
    }
    return groups;
  }, [allDealFiles, items]);

  const handleDownloadSelected = async () => {
    const files = allDealFiles.filter(f => selectedFileIds.has(f.id));
    if (files.length === 0) return;
    setZippingSelected(true);
    try {
      const zip = new JSZip();
      const usedNames = new Set();
      let skipped = 0;
      for (const f of files) {
        if (f.storage_path?.startsWith('sharepoint://')) { skipped++; continue; }
        try {
          const { data, error } = await supabase.storage.from('deal-documents').download(f.storage_path);
          if (error) throw error;
          let name = f.original_name || 'file';
          if (usedNames.has(name)) {
            const dot = name.lastIndexOf('.');
            name = dot > 0 ? `${name.slice(0, dot)} (${f.id.slice(0, 6)})${name.slice(dot)}` : `${name} (${f.id.slice(0, 6)})`;
          }
          usedNames.add(name);
          zip.file(name, data);
        } catch (e) {
          console.warn('Skipping file in zip:', f.original_name, e.message);
          skipped++;
        }
      }
      const blob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(dealName || 'deal').replace(/[^\w\-]+/g, '_')}_selected_files.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      showToast?.(skipped > 0
        ? `Downloaded ${files.length - skipped} file(s) — ${skipped} SharePoint-only file(s) skipped, open those individually`
        : `Downloaded ${files.length} file(s)`);
    } catch (e) {
      showToast?.('Download selected failed: ' + e.message, 'error');
    } finally {
      setZippingSelected(false);
    }
  };

  const handleDownloadFile = async (f) => {
    try {
      const url = await resolveFileUrl(f);
      // Cross-origin storage URLs ignore <a download>'s filename — fetch
      // to a blob first so the saved file gets f.original_name, not the
      // randomized storage key.
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl; a.download = f.original_name || 'file';
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) { showToast?.('Download failed: ' + e.message, 'error'); }
  };

  // Bulk download — deal_advisor has no Documents tab at all (Diligence
  // is their only view), so this is the only way for them to grab
  // everything at once rather than opening each attached file one by one.
  // Scoped to files actually attached to this checklist (what canAct
  // already sees), not every file on the deal.
  const handleDownloadAll = async () => {
    const seen = new Set();
    const allFiles = [];
    items.forEach(item => (item.files || []).forEach(f => {
      if (!seen.has(f.id)) { seen.add(f.id); allFiles.push(f); }
    }));
    if (allFiles.length === 0) { showToast?.('No files to download'); return; }
    setZipping(true);
    try {
      const zip = new JSZip();
      const usedNames = new Set();
      let skipped = 0;
      for (const f of allFiles) {
        if (f.storage_path?.startsWith('sharepoint://')) { skipped++; continue; }
        try {
          const { data, error } = await supabase.storage.from('deal-documents').download(f.storage_path);
          if (error) throw error;
          let name = f.original_name || 'file';
          if (usedNames.has(name)) {
            const dot = name.lastIndexOf('.');
            name = dot > 0 ? `${name.slice(0, dot)} (${f.id.slice(0, 6)})${name.slice(dot)}` : `${name} (${f.id.slice(0, 6)})`;
          }
          usedNames.add(name);
          zip.file(name, data);
        } catch (e) {
          console.warn('Skipping file in zip:', f.original_name, e.message);
          skipped++;
        }
      }
      const blob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(dealName || 'deal').replace(/[^\w\-]+/g, '_')}_diligence_files.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      showToast?.(skipped > 0
        ? `Downloaded ${allFiles.length - skipped} file(s) — ${skipped} SharePoint-only file(s) skipped, open those individually`
        : `Downloaded ${allFiles.length} file(s)`);
    } catch (e) {
      showToast?.('Download all failed: ' + e.message, 'error');
    } finally {
      setZipping(false);
    }
  };

  // General dropzone — not tied to a specific DD item. Uploads land on
  // the deal with no dd_item_id, then auto-categorize-document +
  // ai-match-dd-item run (same pair the admin Documents-tab dropzone
  // fires) to sort/file it and match it to the right checklist item
  // automatically. useDealDdItems already subscribes to both tables'
  // realtime changes, so the matched file shows up on its item without
  // any manual refresh here.
  const handleGenericFiles = async (fileList) => {
    if (!fileList || fileList.length === 0 || genericUploading) return;
    const files = Array.from(fileList);
    setGenericUploading(true);
    const spUrl = import.meta.env.VITE_SUPABASE_URL;
    const spKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    let failures = 0;
    for (const file of files) {
      try {
        const ext = file.name.includes('.') ? file.name.split('.').pop() : '';
        const storageName = Date.now() + '-' + Math.random().toString(36).slice(2, 9) + (ext ? '.' + ext : '');
        const storagePath = `${organizationId}/${dealId}/dd-general/${storageName}`;
        const up = await supabase.storage
          .from('deal-documents')
          .upload(storagePath, file, { cacheControl: '3600', upsert: false });
        if (up.error) throw up.error;

        const ins = await supabase.from('files').insert({
          deal_id: dealId,
          organization_id: organizationId,
          name: storageName,
          original_name: file.name,
          storage_path: storagePath,
          size_bytes: file.size,
          mime_type: file.type || 'application/octet-stream',
          uploaded_by_id: userProfile?.id || null,
          uploaded_by_name: userProfile?.full_name || userProfile?.email || 'User',
          uploaded_by_type: 'user',
        }).select('id').single();
        if (ins.error) throw ins.error;

        // Extract PDF/XLSX text in the browser first — without this, any
        // real-text PDF over 4MB fails server-side as "too large for
        // vision" even though it isn't actually a scan, and large XLSX
        // files risk blowing the edge function's memory limit parsing
        // server-side (the exact HTTP 546 failure already fixed for PDFs).
        const pdfExtra = await extractPdfExtraFields(file);
        const xlsxExtra = await extractXlsxExtraFields(file);
        const bodyPayload = { file_id: ins.data.id, deal_id: dealId, ...pdfExtra, ...xlsxExtra };
        fetch(`${spUrl}/functions/v1/auto-categorize-document`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', apikey: spKey },
          body: JSON.stringify(bodyPayload),
        }).catch(() => {});
        fetch(`${spUrl}/functions/v1/ai-match-dd-item`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', apikey: spKey },
          body: JSON.stringify(bodyPayload),
        }).catch(() => {});

        try { await uploadToSharePoint(file, dealName, file.name, null); }
        catch (spErr) { console.warn('SharePoint sync failed for ' + file.name + ':', spErr.message); }
      } catch (e) {
        console.error('Upload failed for ' + file.name, e);
        failures++;
      }
    }
    setGenericUploading(false);
    if (genericFileInputRef.current) genericFileInputRef.current.value = '';
    if (failures === 0) showToast?.(`Uploaded ${files.length} file(s) — AI is sorting and matching them`);
    else showToast?.(`${files.length - failures} of ${files.length} uploaded — ${failures} failed`, failures === files.length ? 'error' : 'warning');
  };

  // Phase C.2 — honor external trigger from the milestone timeline.
  // When DealView sets pendingSendScope to a role, open the modal and
  // signal back so DealView can clear the trigger.
  useEffect(() => {
    if (pendingSendScope && !showSendModal) {
      setExpanded(true);
      setShowSendModal(pendingSendScope);
      onConsumePendingSendScope?.();
    }
  }, [pendingSendScope]); // eslint-disable-line react-hooks/exhaustive-deps

  const grouped = useMemo(() => {
    const filtered = assigneeFilter === 'all'
      ? items
      : items.filter(i => i.assigned_to === assigneeFilter);
    const bySection = {};
    filtered.forEach(i => {
      if (!bySection[i.section]) {
        bySection[i.section] = { section: i.section, section_sort: i.section_sort, items: [] };
      }
      bySection[i.section].items.push(i);
    });
    return Object.values(bySection).sort((a, b) => a.section_sort - b.section_sort);
  }, [items, assigneeFilter]);

  // Summary by inferred role (legal templates have A./B./... section prefixes).
  // "Done" counts anything past not_started: received (file matched but not
  // yet reviewed), complete (reviewed/approved), or n_a (skipped). The AI
  // file→DD matcher sets received, so it shouldn't show 0 progress just
  // because nothing has been manually marked complete yet.
  //
  // Bucketed by assigned_to (Seller/Counsel/Accountant/Broker) — the same
  // grouping the filter pills below use. This used to be a separate
  // Legal/Financial/Seller split derived from section-letter prefix and
  // template_id, which didn't line up with the pills (e.g. an
  // Accountant-assigned item could land in "Legal" if its section
  // happened to use an A-G prefix) — same items, two different-looking
  // counts, confusing about what's being measured for whom.
  const summaryByAssignee = useMemo(() => {
    const buckets = {};
    items.forEach(i => {
      const key = i.assigned_to || 'seller';
      const bucket = (buckets[key] ||= { total: 0, done: 0 });
      bucket.total += 1;
      if (i.status === 'complete' || i.status === 'n_a' || i.status === 'received') bucket.done += 1;
    });
    return buckets;
  }, [items]);

  const hasItems = items.length > 0;
  const canEdit = !isViewer;
  const canSeed = !isViewer && isAdmin;
  const canAct = canEdit || isDealAdvisor;

  const totalDone = items.filter(i => i.status === 'complete' || i.status === 'n_a' || i.status === 'received').length;
  const totalItems = items.length;
  const pctDone = totalItems > 0 ? Math.round((totalDone / totalItems) * 100) : 0;

  return (
    <section
      className="rounded-2xl mb-6 overflow-hidden relative"
      style={{
        backgroundColor: '#ffffff',
        border: '1px solid #E5E9F0',
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04), 0 4px 12px rgba(15, 23, 42, 0.03)',
      }}
    >
      {/* Accent strip — navy gradient matching timeline */}
      <div
        className="absolute top-0 left-0 right-0 h-[3px]"
        style={{ background: `linear-gradient(90deg, ${primaryColor} 0%, #2E4B7C 30%, #5470A8 70%, ${primaryColor}40 100%)` }}
      />

      {/* Header */}
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-6 py-5 hover:bg-[#FAFBFD] transition-colors text-left"
      >
        <div className="flex items-center gap-3 flex-wrap">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{
              background: `linear-gradient(135deg, ${primaryColor} 0%, #2E4B7C 100%)`,
              boxShadow: `0 2px 6px ${primaryColor}40`,
            }}
          >
            <ClipboardList className="w-4 h-4 text-white" strokeWidth={2.2} />
          </div>
          <div>
            <h2 className="text-base font-semibold tracking-tight" style={{ color: '#0F172A', letterSpacing: '-0.01em' }}>
              Due Diligence
            </h2>
            <div className="flex items-center gap-2 text-xs mt-0.5" style={{ color: '#64748B' }}>
              {hasItems ? (
                <>
                  <span className="tabular-nums" title="All items on this checklist, any assignee">{totalDone} of {totalItems} received · {pctDone}%</span>
                  {ASSIGNEES.filter(a => a.id !== 'all').map(a => {
                    const bucket = summaryByAssignee[a.id];
                    if (!bucket || bucket.total === 0) return null;
                    const color = { seller: '#475569', counsel: '#1A3665', accountant: '#059669', broker: '#A16207' }[a.id] || '#475569';
                    return (
                      <React.Fragment key={a.id}>
                        <span style={{ color: '#CBD5E1' }}>·</span>
                        <span title={`Items assigned to ${a.label}`}>{a.label} <span className="tabular-nums font-medium" style={{ color }}>{bucket.done}/{bucket.total}</span></span>
                      </React.Fragment>
                    );
                  })}
                </>
              ) : (
                <span>No items yet — seed from a template to begin</span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {isDealAdvisor && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); if (!notifyLoading) toggleNotifyOnUpload(); }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); if (!notifyLoading) toggleNotifyOnUpload(); } }}
              className="h-8 inline-flex items-center gap-1.5 px-3 text-xs font-medium rounded-lg transition-colors cursor-pointer disabled:opacity-50"
              style={notifyOnUpload
                ? { backgroundColor: 'rgba(26, 54, 101, 0.08)', color: primaryColor, border: `1px solid ${primaryColor}25` }
                : { backgroundColor: 'white', color: '#475569', border: '1px solid #E5E9F0' }}
              title={notifyOnUpload ? 'Notifying you when files are uploaded to this deal — click to turn off' : 'Get emailed when files are uploaded to this deal'}
            >
              {notifyLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Bell className="w-3.5 h-3.5" fill={notifyOnUpload ? 'currentColor' : 'none'} />}
              {notifyOnUpload ? 'Notifying you' : 'Notify me on uploads'}
            </span>
          )}
          {expanded && selectedFileIds.size > 0 && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); if (!zippingSelected) handleDownloadSelected(); }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); if (!zippingSelected) handleDownloadSelected(); } }}
              className="h-8 inline-flex items-center gap-1.5 px-3 text-xs font-medium rounded-lg transition-colors cursor-pointer disabled:opacity-50"
              style={{ backgroundColor: 'rgba(26, 54, 101, 0.08)', color: primaryColor, border: `1px solid ${primaryColor}25` }}
              title="Download the files you've checked, wherever they were selected"
            >
              {zippingSelected ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FolderDown className="w-3.5 h-3.5" />}
              {zippingSelected ? 'Zipping…' : `Download selected (${selectedFileIds.size})`}
            </span>
          )}
          {expanded && hasItems && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); if (!zipping) handleDownloadAll(); }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); if (!zipping) handleDownloadAll(); } }}
              className="h-8 inline-flex items-center gap-1.5 px-3 text-xs font-medium rounded-lg transition-colors cursor-pointer disabled:opacity-50"
              style={{ backgroundColor: 'white', color: '#475569', border: '1px solid #E5E9F0' }}
              title="Download every attached file as a zip"
            >
              {zipping ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FolderDown className="w-3.5 h-3.5" />}
              {zipping ? 'Zipping…' : 'Download All'}
            </span>
          )}
          {canSeed && expanded && hasItems && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); setShowSeedModal(true); }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); setShowSeedModal(true); } }}
              className="h-8 inline-flex items-center gap-1.5 px-3 text-xs font-medium rounded-lg transition-colors cursor-pointer"
              style={{ backgroundColor: 'rgba(26, 54, 101, 0.08)', color: primaryColor, border: `1px solid ${primaryColor}25` }}
              title="Seed from a diligence template"
            >
              <Plus className="w-3.5 h-3.5" />
              Seed templates
            </span>
          )}
          {expanded ? <ChevronDown className="w-4 h-4" style={{ color: '#94A3B8' }} /> : <ChevronRight className="w-4 h-4" style={{ color: '#94A3B8' }} />}
        </div>
      </button>

      {expanded && (
        <div style={{ borderTop: '1px solid #F0F2F6' }}>
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="w-5 h-5 animate-spin" style={{ color: '#94A3B8' }} />
            </div>
          ) : !hasItems ? (
            <div className="px-6 py-12 text-center">
              <div
                className="w-12 h-12 rounded-xl mx-auto mb-3 flex items-center justify-center"
                style={{ backgroundColor: `${primaryColor}10` }}
              >
                <ClipboardList className="w-6 h-6" style={{ color: primaryColor }} />
              </div>
              <p className="text-sm font-medium mb-1" style={{ color: '#0F172A' }}>
                No diligence items yet
              </p>
              <p className="text-xs mb-4" style={{ color: '#64748B' }}>
                Seed from a template to build the checklist.
              </p>
              {canSeed && (
                <button
                  type="button"
                  onClick={() => setShowSeedModal(true)}
                  className="h-9 inline-flex items-center gap-1.5 px-4 text-sm font-medium rounded-lg text-white transition-all hover:shadow-md"
                  style={{
                    background: `linear-gradient(135deg, ${primaryColor} 0%, #2E4B7C 100%)`,
                    boxShadow: `0 1px 2px ${primaryColor}40, 0 2px 4px ${primaryColor}20`,
                  }}
                >
                  <Plus className="w-4 h-4" />
                  Seed from templates
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Send requests toolbar */}
              {!isViewer && (
                <div
                  className="px-6 py-3.5 flex items-center gap-2 flex-wrap"
                  style={{ backgroundColor: 'rgba(26, 54, 101, 0.03)', borderBottom: '1px solid #F0F2F6' }}
                >
                  <span className="text-[11px] font-semibold uppercase tracking-wider mr-1" style={{ color: '#64748B' }}>
                    Send requests
                  </span>
                  {ASSIGNEES.filter(a => a.id !== 'all').map(a => {
                    const scopeContact = contacts?.[a.id];
                    const pendingItems = items.filter(i => i.assigned_to === a.id && i.status === 'not_started');
                    const hasEmail = !!scopeContact?.email;
                    const canSend = hasEmail && pendingItems.length > 0;
                    const reason = !hasEmail
                      ? `No email set for ${a.label} on this deal`
                      : pendingItems.length === 0
                        ? `No pending items for ${a.label}`
                        : `Email ${scopeContact.email} with ${pendingItems.length} pending item(s)`;
                    return (
                      <button
                        key={a.id}
                        type="button"
                        onClick={() => canSend && setShowSendModal(a.id)}
                        disabled={!canSend}
                        title={reason}
                        className="h-8 inline-flex items-center gap-1.5 px-3 text-xs font-medium rounded-lg transition-all disabled:cursor-not-allowed"
                        style={canSend
                          ? {
                              background: `linear-gradient(135deg, ${primaryColor} 0%, #2E4B7C 100%)`,
                              color: 'white',
                              boxShadow: `0 1px 2px ${primaryColor}40`,
                            }
                          : {
                              backgroundColor: 'white',
                              color: '#94A3B8',
                              border: '1px solid #E5E9F0',
                            }}
                      >
                        <Send className="w-3 h-3" />
                        {a.label}
                        {pendingItems.length > 0 && (
                          <span
                            className="tabular-nums px-1.5 rounded text-[10px] font-semibold"
                            style={canSend
                              ? { backgroundColor: 'rgba(255,255,255,0.22)' }
                              : { backgroundColor: '#F1F5F9', color: '#94A3B8' }}
                          >
                            {pendingItems.length}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Filter */}
              <div
                className="px-6 py-3 flex items-center gap-1.5 flex-wrap"
                style={{ backgroundColor: '#FAFBFD', borderBottom: '1px solid #F0F2F6' }}
              >
                <span className="text-[11px] font-semibold uppercase tracking-wider mr-1" style={{ color: '#94A3B8' }}>
                  Filter
                </span>
                {ASSIGNEES.map(a => {
                  const count = a.id === 'all' ? items.length : items.filter(i => i.assigned_to === a.id).length;
                  const isActive = assigneeFilter === a.id;
                  return (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => setAssigneeFilter(a.id)}
                      className="h-7 inline-flex items-center gap-1.5 px-3 text-xs font-medium rounded-full transition-all"
                      style={isActive
                        ? {
                            background: `linear-gradient(135deg, ${primaryColor} 0%, #2E4B7C 100%)`,
                            color: 'white',
                            boxShadow: `0 1px 2px ${primaryColor}30`,
                          }
                        : {
                            backgroundColor: 'white',
                            color: '#64748B',
                            border: '1px solid #E5E9F0',
                          }}
                    >
                      {a.label}
                      <span
                        className="tabular-nums text-[10px] font-semibold"
                        style={isActive
                          ? { color: 'rgba(255,255,255,0.8)' }
                          : { color: '#94A3B8' }}
                      >
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>

              {canAct && (
                <div className="px-6 pt-4">
                  <input
                    ref={genericFileInputRef}
                    type="file"
                    multiple
                    className="hidden"
                    onChange={(e) => handleGenericFiles(e.target.files)}
                  />
                  <div
                    onClick={() => genericFileInputRef.current?.click()}
                    onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                    onDragLeave={() => setDragActive(false)}
                    onDrop={(e) => { e.preventDefault(); setDragActive(false); handleGenericFiles(e.dataTransfer.files); }}
                    className="rounded-xl border-2 border-dashed flex flex-col items-center justify-center gap-1.5 py-6 cursor-pointer transition-colors"
                    style={{
                      borderColor: dragActive ? primaryColor : '#DBE6F5',
                      backgroundColor: dragActive ? `${primaryColor}0D` : '#FAFBFD',
                    }}
                  >
                    {genericUploading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" style={{ color: primaryColor }} />
                        <span className="text-xs font-medium" style={{ color: '#475569' }}>Uploading…</span>
                      </>
                    ) : (
                      <>
                        <Upload className="w-5 h-5" style={{ color: primaryColor }} />
                        <span className="text-xs font-medium" style={{ color: '#475569' }}>
                          Drag files here or click to upload
                        </span>
                        <span className="text-[11px]" style={{ color: '#94A3B8' }}>
                          AI will sort, file, and match each one to the right checklist item automatically
                        </span>
                      </>
                    )}
                  </div>
                </div>
              )}

              <div>
                {grouped.length === 0 ? (
                  <div className="px-6 py-10 text-center text-sm" style={{ color: '#94A3B8' }}>
                    No items for this filter.
                  </div>
                ) : grouped.map(group => (
                  <DdSectionGroup
                    key={group.section}
                    group={group}
                    canEdit={canEdit}
                    canAct={canAct}
                    isAdmin={isAdmin}
                    allItems={items}
                    expandedItemId={expandedItemId}
                    setExpandedItemId={setExpandedItemId}
                    updateItem={updateItem}
                    deleteItem={deleteItem}
                    uploadDdItemFile={uploadDdItemFile}
                    userProfile={userProfile}
                    dealName={dealName}
                    showToast={showToast}
                    primaryColor={primaryColor}
                    selectedFileIds={selectedFileIds}
                    toggleFileSelected={toggleFileSelected}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* All files — deal_advisor has no Documents tab, so this is their
          only way to browse and download every file on the deal (matched
          to a checklist item or not), with an uploader/date audit trail
          and multi-select bulk download, at parity with the internal
          FilesView. Grouped by checklist request by default (Andy/Cohen &
          Co: target companies rarely name files identifiably, so knowing
          which request # a file answers matters more than a flat list). */}
      {expanded && allDealFiles.length > 0 && (
        <div className="px-6 py-4" style={{ borderTop: '1px solid #F0F2F6', backgroundColor: '#FAFBFD' }}>
          <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={selectedFileIds.size > 0 && selectedFileIds.size === allDealFiles.length}
                ref={el => { if (el) el.indeterminate = selectedFileIds.size > 0 && selectedFileIds.size < allDealFiles.length; }}
                onChange={() => setSelectedFileIds(
                  selectedFileIds.size === allDealFiles.length ? new Set() : new Set(allDealFiles.map(f => f.id))
                )}
                title="Select all"
              />
              <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#64748B' }}>
                All files ({allDealFiles.length})
              </span>
              <span className="text-[11px]" style={{ color: '#94A3B8' }}>
                — every file uploaded to this deal
              </span>
            </div>
            <div className="flex items-center gap-2">
              {selectedFileIds.size > 0 && (
                <span className="text-[11px]" style={{ color: '#94A3B8' }}>
                  {selectedFileIds.size} selected — "Download selected" is up in the header
                </span>
              )}
              <div className="inline-flex rounded-md overflow-hidden" style={{ border: '1px solid #E5E9F0' }}>
                <button
                  type="button"
                  onClick={() => setGroupFilesByRequest(true)}
                  className="px-2 py-1 text-[11px] font-medium"
                  style={groupFilesByRequest ? { backgroundColor: primaryColor, color: 'white' } : { backgroundColor: 'white', color: '#64748B' }}
                >
                  By request
                </button>
                <button
                  type="button"
                  onClick={() => setGroupFilesByRequest(false)}
                  className="px-2 py-1 text-[11px] font-medium"
                  style={!groupFilesByRequest ? { backgroundColor: primaryColor, color: 'white' } : { backgroundColor: 'white', color: '#64748B' }}
                >
                  Flat list
                </button>
              </div>
            </div>
          </div>
          {groupFilesByRequest ? (
            <div className="space-y-3">
              {fileGroups.map(group => (
                <div key={group.key}>
                  <div className="flex items-baseline gap-2 mb-1 px-0.5" title={group.detail || ''}>
                    <span className="text-[11px] font-semibold" style={{ color: group.key === 'unmatched' ? '#94A3B8' : '#1A3665' }}>
                      {group.label}
                    </span>
                    {group.detail && (
                      <span className="text-[10px] truncate" style={{ color: '#94A3B8', maxWidth: 420 }}>
                        {group.detail}
                      </span>
                    )}
                    <span className="text-[10px] tabular-nums" style={{ color: '#CBD5E1' }}>({group.files.length})</span>
                  </div>
                  <div className="space-y-1">
                    {group.files.map(f => (
                      <FileRow key={f.id} f={f} selectedFileIds={selectedFileIds} toggleFileSelected={toggleFileSelected} onDownload={handleDownloadFile} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
          <div className="space-y-1">
            {allDealFiles.map(f => (
              <FileRow key={f.id} f={f} selectedFileIds={selectedFileIds} toggleFileSelected={toggleFileSelected} onDownload={handleDownloadFile} showUnmatchedTag />
            ))}
          </div>
          )}
        </div>
      )}

      {showSeedModal && (
        <SeedTemplatesModal
          onClose={() => setShowSeedModal(false)}
          seedFromTemplates={seedFromTemplates}
          primaryColor={primaryColor}
          showToast={showToast}
        />
      )}

      {showSendModal && (
        <SendDdRequestModal
          scope={showSendModal}
          contact={contacts?.[showSendModal]}
          allContacts={contacts}
          items={items.filter(i => i.assigned_to === showSendModal && i.status === 'not_started')}
          dealId={dealId}
          dealName={dealName}
          userProfile={userProfile}
          primaryColor={primaryColor}
          updateItem={updateItem}
          onClose={() => setShowSendModal(null)}
          showToast={showToast}
        />
      )}
    </section>
  );
}

function FileRow({ f, selectedFileIds, toggleFileSelected, onDownload, showUnmatchedTag }) {
  return (
    <div className="flex items-center gap-2 text-xs bg-white border rounded px-2 py-1" style={{ borderColor: '#E5E9F0' }}>
      <input
        type="checkbox"
        checked={selectedFileIds.has(f.id)}
        onChange={() => toggleFileSelected(f.id)}
        className="flex-shrink-0"
      />
      <FileText className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
      <span className="flex-1 truncate">{f.original_name}</span>
      {showUnmatchedTag && !f.dd_item_id && (
        <span className="text-[9px] px-1 rounded flex-shrink-0" style={{ backgroundColor: '#F1F5F9', color: '#94A3B8' }} title="Not attached to a checklist item">
          unmatched
        </span>
      )}
      <span
        className="text-gray-400 text-[10px] flex-shrink-0"
        title={`Uploaded by ${f.uploaded_by_name || f.uploaded_by_type || 'unknown'} on ${f.created_at ? new Date(f.created_at).toLocaleString() : 'unknown date'}`}
      >
        {(f.uploaded_by_name || f.uploaded_by_type || '—')} · {f.created_at ? new Date(f.created_at).toLocaleDateString() : '—'}
      </span>
      <span className="text-gray-400 text-[10px] flex-shrink-0">{((f.size_bytes || 0) / 1024).toFixed(0)} KB</span>
      <button
        type="button"
        onClick={() => onDownload(f)}
        className="text-gray-400 hover:text-blue-600 flex-shrink-0"
        title="Download this file"
      >
        <Download className="w-3 h-3" />
      </button>
    </div>
  );
}

function DdSectionGroup({ group, canEdit, canAct, isAdmin, allItems, expandedItemId, setExpandedItemId, updateItem, deleteItem, uploadDdItemFile, userProfile, dealName, showToast, primaryColor, selectedFileIds, toggleFileSelected }) {
  const [collapsed, setCollapsed] = useState(false);
  // Match the top-level summary: count anything past not_started as progress.
  const doneCount = group.items.filter(i => i.status === 'complete' || i.status === 'n_a' || i.status === 'received').length;
  const pct = Math.round((doneCount / group.items.length) * 100);

  return (
    <div style={{ borderBottom: '1px solid #F0F2F6' }}>
      <button
        type="button"
        onClick={() => setCollapsed(!collapsed)}
        className="w-full flex items-center justify-between px-6 py-2.5 hover:bg-[#FAFBFD] transition-colors text-left"
        style={{ backgroundColor: '#FCFCFE' }}
      >
        <div className="flex items-center gap-2.5">
          {collapsed
            ? <ChevronRight className="w-3.5 h-3.5" style={{ color: '#94A3B8' }} />
            : <ChevronDown className="w-3.5 h-3.5" style={{ color: '#94A3B8' }} />
          }
          <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#475569' }}>
            {group.section}
          </span>
          <span className="text-[10px] tabular-nums" style={{ color: '#94A3B8' }}>
            {doneCount}/{group.items.length}
          </span>
          {/* Tiny progress bar */}
          {group.items.length > 0 && (
            <div className="w-16 h-1 rounded-full overflow-hidden" style={{ backgroundColor: '#E5E9F0' }}>
              <div
                className="h-full transition-all"
                style={{
                  width: `${pct}%`,
                  background: pct === 100
                    ? 'linear-gradient(90deg, #10B981, #059669)'
                    : `linear-gradient(90deg, ${primaryColor}, #5470A8)`,
                }}
              />
            </div>
          )}
        </div>
      </button>
      {!collapsed && (
        <div>
          {group.items.map(item => (
            <DdItemRow
              key={item.id}
              item={item}
              canEdit={canEdit}
              canAct={canAct}
              isAdmin={isAdmin}
              allItems={allItems}
              isExpanded={expandedItemId === item.id}
              onToggleExpand={() => setExpandedItemId(expandedItemId === item.id ? null : item.id)}
              updateItem={updateItem}
              deleteItem={deleteItem}
              uploadDdItemFile={uploadDdItemFile}
              userProfile={userProfile}
              dealName={dealName}
              showToast={showToast}
              primaryColor={primaryColor}
              selectedFileIds={selectedFileIds}
              toggleFileSelected={toggleFileSelected}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function DdItemRow({ item, canEdit, canAct, isAdmin, allItems, isExpanded, onToggleExpand, updateItem, deleteItem, uploadDdItemFile, userProfile, dealName, showToast, primaryColor, selectedFileIds, toggleFileSelected }) {
  const stat = statusMeta(item.status);
  const pri = PRIORITY_STYLES[item.priority] || PRIORITY_STYLES.medium;
  const fileCount = item.files?.length || 0;
  const [showRelinkFor, setShowRelinkFor] = useState(null); // file id being relinked
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleStatusChange = async (newStatus) => {
    try { await updateItem(item.id, { status: newStatus }); }
    catch (e) { showToast?.('Update failed: ' + e.message, 'error'); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const uploaded = await uploadDdItemFile(item.id, file, {
        id: userProfile?.id, name: userProfile?.full_name || userProfile?.email || 'User', type: 'user',
      });
      showToast?.('File uploaded');
      // Non-blocking: SharePoint sync + AI categorization, same as the
      // generic Documents-tab upload and the seller-portal DD upload.
      // Already matched to this item, so no need for ai-match-dd-item.
      uploadToSharePoint(file, dealName, file.name, null).catch(() => {});
      if (uploaded?.id) {
        // Extract PDF/XLSX text in the browser first — same reason as the
        // general dropzone above.
        const pdfExtra = await extractPdfExtraFields(file);
        const xlsxExtra = await extractXlsxExtraFields(file);
        fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/auto-categorize-document`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', apikey: import.meta.env.VITE_SUPABASE_ANON_KEY },
          body: JSON.stringify({ file_id: uploaded.id, deal_id: item.deal_id, ...pdfExtra, ...xlsxExtra }),
        }).catch(() => {});
      }
    } catch (e) {
      showToast?.('Upload failed: ' + e.message, 'error');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleAssigneeChange = async (newAssignee) => {
    try { await updateItem(item.id, { assigned_to: newAssignee }); }
    catch (e) { showToast?.('Update failed: ' + e.message, 'error'); }
  };

  return (
    <div
      className="transition-colors"
      style={{
        borderBottom: '1px solid #F0F2F6',
        backgroundColor: isExpanded ? '#FAFBFD' : '#FFFFFF',
      }}
      onMouseEnter={e => { if (!isExpanded) e.currentTarget.style.backgroundColor = '#FCFCFE'; }}
      onMouseLeave={e => { if (!isExpanded) e.currentTarget.style.backgroundColor = '#FFFFFF'; }}
    >
      <div className="flex items-start gap-3 px-6 py-3">
        <button
          type="button"
          onClick={onToggleExpand}
          className="mt-0.5 w-5 h-5 flex items-center justify-center rounded transition-colors flex-shrink-0 hover:bg-[#E5E9F0]"
          title={isExpanded ? 'Collapse' : 'Expand'}
        >
          {isExpanded
            ? <ChevronDown className="w-3.5 h-3.5" style={{ color: '#64748B' }} />
            : <ChevronRight className="w-3.5 h-3.5" style={{ color: '#94A3B8' }} />
          }
        </button>

        <span
          className="text-[11px] font-mono pt-0.5 flex-shrink-0 w-8 text-right tabular-nums"
          style={{ color: '#94A3B8' }}
        >
          {item.item_number}
        </span>

        <div className="flex-1 min-w-0">
          <div
            className={`text-sm leading-snug ${isExpanded ? '' : 'truncate'}`}
            style={{ color: '#0F172A' }}
          >
            {item.request_text}
          </div>
          {!isExpanded && (
            <div className="flex items-center gap-2 mt-1.5">
              <span
                className="text-[10px] font-medium px-1.5 py-0.5 rounded"
                style={{
                  backgroundColor: item.priority === 'high'   ? '#FEE2E2'
                                 : item.priority === 'medium' ? '#DBEAFE'
                                 : '#F1F5F9',
                  color: item.priority === 'high'   ? '#B91C1C'
                       : item.priority === 'medium' ? '#1E40AF'
                       : '#64748B',
                }}
              >
                {pri.label}
              </span>
              <span className="text-[11px] capitalize" style={{ color: '#64748B' }}>
                {item.assigned_to}
              </span>
              {item.qb_extractable && (
                <span
                  className="text-[10px] font-semibold px-1.5 py-0.5 rounded"
                  style={{ backgroundColor: '#EEF2FF', color: '#4338CA' }}
                  title="Extractable from QuickBooks"
                >
                  QB
                </span>
              )}
              {fileCount > 0 && (
                <span className="inline-flex items-center gap-1 text-[11px]" style={{ color: '#64748B' }}>
                  <FileText className="w-3 h-3" /> {fileCount}
                </span>
              )}
              {item.instructions && (
                <span title="Has seller-facing instructions">
                  <FileText className="w-3 h-3" style={{ color: '#3B82F6' }} />
                </span>
              )}
              {item.internal_comments && (
                <MessageSquare className="w-3 h-3" style={{ color: '#3B82F6' }} title="Has internal notes" />
              )}
              {item.resolution_note && (
                <Check className="w-3 h-3" style={{ color: '#10B981' }} title="Handled by assignee" />
              )}
            </div>
          )}
        </div>

        {canEdit ? (
          <select
            value={item.status}
            onChange={(e) => handleStatusChange(e.target.value)}
            className="h-7 text-[11px] rounded-md px-2 font-semibold cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-1 border-0 transition-all"
            style={{
              minWidth: '108px',
              backgroundColor:
                item.status === 'complete'     ? '#DCFCE7' :
                item.status === 'received'     ? '#EDE9FE' :
                item.status === 'requested'    ? '#FEF3C7' :
                item.status === 'under_review' ? '#FFEDD5' :
                item.status === 'n_a'          ? '#F1F5F9' :
                                                 '#F1F5F9',
              color:
                item.status === 'complete'     ? '#15803D' :
                item.status === 'received'     ? '#6D28D9' :
                item.status === 'requested'    ? '#A16207' :
                item.status === 'under_review' ? '#C2410C' :
                item.status === 'n_a'          ? '#64748B' :
                                                 '#475569',
              boxShadow: 'inset 0 0 0 1px rgba(15, 23, 42, 0.04)',
            }}
          >
            {STATUSES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        ) : (
          <span
            className="h-7 inline-flex items-center text-[11px] px-2 rounded-md font-semibold"
            style={{
              backgroundColor:
                item.status === 'complete'     ? '#DCFCE7' :
                item.status === 'received'     ? '#EDE9FE' :
                item.status === 'requested'    ? '#FEF3C7' :
                item.status === 'under_review' ? '#FFEDD5' :
                item.status === 'n_a'          ? '#F1F5F9' :
                                                 '#F1F5F9',
              color:
                item.status === 'complete'     ? '#15803D' :
                item.status === 'received'     ? '#6D28D9' :
                item.status === 'requested'    ? '#A16207' :
                item.status === 'under_review' ? '#C2410C' :
                item.status === 'n_a'          ? '#64748B' :
                                                 '#475569',
            }}
          >
            {stat.label}
          </span>
        )}
      </div>

      {isExpanded && (
        <div className="px-6 pb-5 space-y-4" style={{ paddingLeft: '4rem' }}>
          <div className="flex items-center gap-4 flex-wrap pt-1">
            <label className="flex items-center gap-2">
              <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#94A3B8' }}>Priority</span>
              <span
                className="text-[10px] font-medium px-1.5 py-0.5 rounded"
                style={{
                  backgroundColor: item.priority === 'high'   ? '#FEE2E2'
                                 : item.priority === 'medium' ? '#DBEAFE'
                                 : '#F1F5F9',
                  color: item.priority === 'high'   ? '#B91C1C'
                       : item.priority === 'medium' ? '#1E40AF'
                       : '#64748B',
                }}
              >
                {pri.label}
              </span>
            </label>
            <label className="flex items-center gap-2">
              <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#94A3B8' }}>Assigned</span>
              {canEdit ? (
                <select
                  value={item.assigned_to}
                  onChange={(e) => handleAssigneeChange(e.target.value)}
                  className="h-7 text-xs rounded-md px-2 bg-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-0"
                  style={{ border: '1px solid #E5E9F0', color: '#0F172A' }}
                >
                  {ASSIGNEES.filter(a => a.id !== 'all').map(a => (
                    <option key={a.id} value={a.id}>{a.label}</option>
                  ))}
                </select>
              ) : (
                <span className="text-xs font-medium capitalize" style={{ color: '#0F172A' }}>{item.assigned_to}</span>
              )}
            </label>
            {item.qb_extractable && (
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded" style={{ backgroundColor: '#EEF2FF', color: '#4338CA' }}>
                QB Extractable
              </span>
            )}
          </div>

          <InstructionsEditor
            item={item}
            canEdit={canEdit}
            updateItem={updateItem}
            showToast={showToast}
          />

          {/* deal_advisor doesn't get the full status dropdown (canEdit-only,
              above) but can still mark something N/A — a scoped action
              instead of open status control, same idea as the seller
              portal's dedicated Mark N/A button rather than a raw dropdown. */}
          {canAct && !canEdit && (
            <div className="flex items-center gap-2">
              {item.status === 'n_a' ? (
                <button
                  type="button"
                  onClick={() => handleStatusChange('not_started')}
                  className="text-xs px-2.5 py-1 rounded-md border font-medium"
                  style={{ borderColor: '#E5E9F0', color: '#64748B' }}
                >
                  Undo N/A
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => handleStatusChange('n_a')}
                  className="text-xs px-2.5 py-1 rounded-md border font-medium"
                  style={{ borderColor: '#E5E9F0', color: primaryColor }}
                >
                  Mark as N/A
                </button>
              )}
            </div>
          )}

          <ResolutionNoteEditor
            item={item}
            canAct={canAct}
            updateItem={updateItem}
            showToast={showToast}
          />

          <InternalNotesEditor
            item={item}
            canEdit={canEdit}
            updateItem={updateItem}
            showToast={showToast}
          />

          {fileCount > 0 && (
            <div>
              <div className="text-xs font-semibold text-gray-500 mb-1">Attached files ({fileCount})</div>
              <div className="space-y-1">
                {item.files.map(f => {
                  const conf = f.dd_match_confidence;
                  const showConfBadge = typeof conf === 'number' && conf < 0.85;
                  return (
                    <div key={f.id} className="flex items-center gap-2 text-xs bg-white border rounded px-2 py-1">
                      {selectedFileIds && toggleFileSelected && (
                        <input
                          type="checkbox"
                          checked={selectedFileIds.has(f.id)}
                          onChange={() => toggleFileSelected(f.id)}
                          className="flex-shrink-0"
                        />
                      )}
                      <FileText className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                      <span className="flex-1 truncate">{f.original_name}</span>
                      {showConfBadge && (
                        <span
                          className="px-1.5 py-0.5 rounded text-[10px] font-medium flex items-center gap-0.5 flex-shrink-0"
                          style={{
                            backgroundColor: conf < 0.5 ? '#fef3c7' : '#e0e7ff',
                            color: conf < 0.5 ? '#92400e' : '#3730a3',
                          }}
                          title={`AI auto-matched with ${(conf * 100).toFixed(0)}% confidence — verify`}
                        >
                          <Sparkles className="w-2.5 h-2.5" />
                          {(conf * 100).toFixed(0)}%
                        </span>
                      )}
                      <span
                        className="text-gray-400 text-[10px] flex-shrink-0"
                        title={`Uploaded by ${f.uploaded_by_name || f.uploaded_by_type || 'unknown'} on ${f.created_at ? new Date(f.created_at).toLocaleString() : 'unknown date'}`}
                      >
                        {(f.uploaded_by_name || f.uploaded_by_type || '—')} · {f.created_at ? new Date(f.created_at).toLocaleDateString() : '—'}
                      </span>
                      <span className="text-gray-400 text-[10px]">{((f.size_bytes || 0) / 1024).toFixed(0)} KB</span>
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            const url = await resolveFileUrl(f);
                            // Cross-origin storage URLs ignore <a download>'s filename —
                            // fetch to a blob first so the saved file gets f.original_name,
                            // not the randomized storage key.
                            const response = await fetch(url);
                            const blob = await response.blob();
                            const blobUrl = window.URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = blobUrl; a.download = f.original_name || 'file';
                            document.body.appendChild(a); a.click(); document.body.removeChild(a);
                            window.URL.revokeObjectURL(blobUrl);
                          } catch (e) { showToast?.('Download failed: ' + e.message, 'error'); }
                        }}
                        className="text-gray-400 hover:text-blue-600"
                        title="Download this file"
                      >
                        <Download className="w-3 h-3" />
                      </button>
                      {isAdmin && canEdit && (
                        <>
                          <button
                            type="button"
                            onClick={() => setShowRelinkFor(f.id)}
                            className="text-blue-600 hover:text-blue-700"
                            title="Move this file to a different DD item"
                          >
                            <Link2 className="w-3 h-3" />
                          </button>
                          <button
                            type="button"
                            onClick={async () => {
                              if (!window.confirm('Unlink this file from the DD item? The file itself stays on the deal.')) return;
                              try {
                                await relinkDdFile(f.id, null);
                                showToast?.('File unlinked');
                              } catch (e) { showToast?.('Unlink failed: ' + e.message, 'error'); }
                            }}
                            className="text-red-500 hover:text-red-600"
                            title="Unlink file from this item"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {canAct && (
            <div>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={handleFileUpload}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-md border disabled:opacity-50"
                style={{ borderColor: '#E5E9F0', color: primaryColor }}
              >
                {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
                {uploading ? 'Uploading…' : 'Upload a file to this item'}
              </button>
            </div>
          )}

          {showRelinkFor && (
            <RelinkFileModal
              fileId={showRelinkFor}
              currentItemId={item.id}
              allItems={allItems}
              onClose={() => setShowRelinkFor(null)}
              showToast={showToast}
            />
          )}

          {canEdit && (
            <div className="flex items-center gap-2 pt-1">
              <button
                type="button"
                onClick={async () => {
                  if (!window.confirm('Delete this diligence item? Uploaded files will be unlinked but not deleted.')) return;
                  try { await deleteItem(item.id); showToast?.('Item deleted'); }
                  catch (e) { showToast?.('Delete failed: ' + e.message, 'error'); }
                }}
                className="text-xs text-red-600 hover:text-red-700 flex items-center gap-1"
              >
                <Trash2 className="w-3 h-3" /> Delete
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Seller-facing instructions for the item (visible in the portal under the
// request text). Sits above internal notes in the expanded view.
// The note an assignee (seller, or now deal_advisor) leaves when resolving
// an item — same resolution_note column the seller portal's
// record_seller_dd_resolution already writes to. canAct (not canEdit) so
// deal_advisor can add/edit their own note here even though they can't
// reassign the item or edit internal team notes.
function ResolutionNoteEditor({ item, canAct, updateItem, showToast }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(item.resolution_note || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (!editing) setDraft(item.resolution_note || ''); }, [item.resolution_note, editing]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateItem(item.id, { resolution_note: draft || null });
      setEditing(false);
    } catch (e) {
      showToast?.('Save failed: ' + e.message, 'error');
    } finally { setSaving(false); }
  };

  if (!canAct && !item.resolution_note) return null;

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <div className="text-xs font-semibold text-gray-500 flex items-center gap-1">
          {item.resolution_note
            ? (<><Check className="w-3 h-3 text-green-600" /> Handled by {item.assigned_to}</>)
            : 'Resolution note'}
        </div>
        {canAct && !editing && (
          <button type="button" onClick={() => setEditing(true)} className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-0.5">
            <Pencil className="w-3 h-3" /> {item.resolution_note ? 'Edit' : 'Add a note'}
          </button>
        )}
      </div>
      {editing ? (
        <div>
          <textarea
            value={draft}
            onChange={e => setDraft(e.target.value)}
            placeholder="Any context on how this was resolved — e.g. 'Uploaded QBO access grant, see attached file'"
            rows={3}
            className="w-full text-xs border rounded px-2 py-1.5 focus:outline-none focus:ring-1"
          />
          <div className="flex gap-2 mt-1 justify-end">
            <button type="button" onClick={() => { setDraft(item.resolution_note || ''); setEditing(false); }} disabled={saving}
                    className="px-2 py-1 text-xs text-gray-500 hover:bg-gray-100 rounded">Cancel</button>
            <button type="button" onClick={handleSave} disabled={saving}
                    className="px-2 py-1 text-xs text-white rounded bg-blue-600 hover:bg-blue-700 flex items-center gap-1 disabled:opacity-50">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
              Save
            </button>
          </div>
        </div>
      ) : item.resolution_note ? (
        <div className="text-xs text-gray-700 bg-green-50 border border-green-200 rounded px-2 py-1.5 whitespace-pre-wrap">
          {item.resolution_note}
        </div>
      ) : null}
    </div>
  );
}

function InstructionsEditor({ item, canEdit, updateItem, showToast }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(item.instructions || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (!editing) setDraft(item.instructions || ''); }, [item.instructions, editing]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateItem(item.id, { instructions: draft || null });
      setEditing(false);
    } catch (e) {
      showToast?.('Save failed: ' + e.message, 'error');
    } finally { setSaving(false); }
  };

  if (!canEdit && !item.instructions) return null;

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <div className="text-xs font-semibold text-gray-500 flex items-center gap-1">
          <FileText className="w-3 h-3" /> Seller-facing instructions
          <span className="text-gray-400 font-normal">(visible in portal)</span>
        </div>
        {canEdit && !editing && (
          <button type="button" onClick={() => setEditing(true)} className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-0.5">
            <Pencil className="w-3 h-3" /> {item.instructions ? 'Edit' : 'Add'}
          </button>
        )}
      </div>
      {editing ? (
        <div>
          <textarea
            value={draft}
            onChange={e => setDraft(e.target.value)}
            placeholder="Extra guidance for whoever fulfills this — e.g. 'Either grant QBO access OR upload .qbb file'"
            rows={4}
            className="w-full text-xs border rounded px-2 py-1.5 focus:outline-none focus:ring-1"
          />
          <div className="flex gap-2 mt-1 justify-end">
            <button type="button" onClick={() => { setDraft(item.instructions || ''); setEditing(false); }} disabled={saving}
                    className="px-2 py-1 text-xs text-gray-500 hover:bg-gray-100 rounded">Cancel</button>
            <button type="button" onClick={handleSave} disabled={saving}
                    className="px-2 py-1 text-xs text-white rounded bg-blue-600 hover:bg-blue-700 flex items-center gap-1 disabled:opacity-50">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
              Save
            </button>
          </div>
        </div>
      ) : (
        <div className="text-xs text-gray-600 bg-blue-50 border border-blue-100 rounded px-2 py-1.5 whitespace-pre-wrap min-h-[24px]">
          {item.instructions || <span className="text-gray-400 italic">No instructions yet</span>}
        </div>
      )}
    </div>
  );
}

function InternalNotesEditor({ item, canEdit, updateItem, showToast }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(item.internal_comments || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!editing) setDraft(item.internal_comments || '');
  }, [item.internal_comments, editing]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateItem(item.id, { internal_comments: draft });
      setEditing(false);
    } catch (e) {
      showToast?.('Save failed: ' + e.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  if (!canEdit && !item.internal_comments) return null;

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <div className="text-xs font-semibold text-gray-500 flex items-center gap-1">
          <MessageSquare className="w-3 h-3" /> Internal notes
        </div>
        {canEdit && !editing && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-0.5"
          >
            <Pencil className="w-3 h-3" /> {item.internal_comments ? 'Edit' : 'Add'}
          </button>
        )}
      </div>
      {editing ? (
        <div>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Notes visible only to internal team (not shared with seller/contacts)..."
            rows={3}
            className="w-full text-xs border rounded px-2 py-1.5 focus:outline-none focus:ring-1"
          />
          <div className="flex gap-2 mt-1 justify-end">
            <button
              type="button"
              onClick={() => { setDraft(item.internal_comments || ''); setEditing(false); }}
              disabled={saving}
              className="px-2 py-1 text-xs text-gray-500 hover:bg-gray-100 rounded"
            >Cancel</button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="px-2 py-1 text-xs text-white rounded bg-blue-600 hover:bg-blue-700 flex items-center gap-1 disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
              Save
            </button>
          </div>
        </div>
      ) : (
        <div className="text-xs text-gray-600 bg-gray-50 rounded px-2 py-1.5 whitespace-pre-wrap min-h-[24px]">
          {item.internal_comments || <span className="text-gray-400 italic">No notes</span>}
        </div>
      )}
    </div>
  );
}

function SeedTemplatesModal({ onClose, seedFromTemplates, primaryColor, showToast }) {
  const { templates, loading } = useDdTemplates();
  const [selected, setSelected] = useState(new Set());
  const [seeding, setSeeding] = useState(false);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const handleSeed = async () => {
    if (selected.size === 0) return;
    setSeeding(true);
    try {
      const result = await seedFromTemplates([...selected]);
      const msg = result.skipped
        ? `Added ${result.inserted} items (${result.skipped} already existed)`
        : `Added ${result.inserted} items`;
      showToast?.(msg);
      onClose();
    } catch (e) {
      showToast?.('Seed failed: ' + e.message, 'error');
    } finally {
      setSeeding(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-lg shadow-xl max-w-lg w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <h3 className="font-semibold text-base" style={{ color: primaryColor }}>Seed diligence items from templates</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-5 py-4 max-h-96 overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
          ) : templates.length === 0 ? (
            <p className="text-sm text-gray-500">No active templates found.</p>
          ) : (
            <>
              <p className="text-xs text-gray-500 mb-3">
                Re-seeding is safe — items already added from a template won't be duplicated.
              </p>
              <div className="space-y-2">
                {templates.map(t => (
                  <label
                    key={t.id}
                    className="flex items-start gap-3 p-3 border rounded cursor-pointer hover:bg-gray-50 transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(t.id)}
                      onChange={() => toggle(t.id)}
                      className="mt-0.5 accent-blue-600"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">{t.name}</span>
                        <span className="px-1.5 py-0.5 rounded text-[10px] bg-gray-100 text-gray-600 uppercase font-medium">{t.role}</span>
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">{t.item_count} items</div>
                      {t.description && (
                        <div className="text-xs text-gray-500 mt-1">{t.description}</div>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            </>
          )}
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={seeding}
            className="px-3 py-1.5 text-sm border rounded hover:bg-gray-50 disabled:opacity-50"
          >Cancel</button>
          <button
            type="button"
            onClick={handleSeed}
            disabled={seeding || selected.size === 0}
            className="px-3 py-1.5 text-sm text-white rounded hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center gap-1.5"
            style={{ backgroundColor: primaryColor }}
          >
            {seeding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
            Seed {selected.size > 0 ? `(${selected.size})` : ''}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SendDdRequestModal
// Builds a scoped portal token for the given assignee, flips the selected
// items to `requested`, and sends an HTML email with the portal link and a
// section-grouped item list. Mirrors the task-based send flow in
// DealView.handleSendRequest for UX consistency.
// ---------------------------------------------------------------------------

const SCOPE_LABELS = {
  seller:     { label: 'Seller',           phrase: 'As the seller for' },
  counsel:    { label: 'Seller Counsel',   phrase: 'As counsel for the seller in' },
  accountant: { label: 'Accountant / CPA', phrase: 'As accountant / CPA for the seller in' },
  broker:     { label: 'Broker',           phrase: 'As broker / representative for the seller in' },
};

function SendDdRequestModal({
  scope, contact, allContacts, items, dealId, dealName, userProfile, primaryColor,
  updateItem, onClose, showToast,
}) {
  const meta = SCOPE_LABELS[scope] || SCOPE_LABELS.seller;
  const [emailTo, setEmailTo] = useState(contact?.email || '');

  // Build the candidate CC list from every known deal contact + the
  // requesting LFG user. The primary recipient (emailTo) is auto-excluded.
  const ccCandidates = useMemo(() => {
    const list = [];
    const seen = new Set();
    const push = (label, role, name, email) => {
      const e = (email || '').trim().toLowerCase();
      if (!e || !e.includes('@') || seen.has(e)) return;
      seen.add(e);
      list.push({ key: e, label, role, name: name || '', email: e });
    };
    if (allContacts?.seller)     push('Seller',           'seller',     allContacts.seller.name,     allContacts.seller.email);
    if (allContacts?.counsel)    push('Seller Counsel',   'counsel',    allContacts.counsel.name,    allContacts.counsel.email);
    if (allContacts?.accountant) push('Seller CPA',       'accountant', allContacts.accountant.name, allContacts.accountant.email);
    if (allContacts?.broker)     push('Broker',           'broker',     allContacts.broker.name,     allContacts.broker.email);
    if (userProfile?.email)      push('You (LFG)',        'lfg',        userProfile.full_name,       userProfile.email);
    return list;
  }, [allContacts, userProfile?.email, userProfile?.full_name]);

  // Default-check every CC candidate that isn't the primary recipient.
  // Mirrors the user's stated workflow ("send to seller, but everyone else
  // should be on the email too") — they can uncheck individuals if needed.
  const [ccChecked, setCcChecked] = useState(() => {
    const init = new Set();
    const primary = (contact?.email || '').toLowerCase();
    ccCandidates.forEach(c => { if (c.email !== primary) init.add(c.email); });
    return init;
  });
  const [extraCcInput, setExtraCcInput] = useState('');

  // When the primary recipient changes, drop them from the CC set.
  useEffect(() => {
    const primary = (emailTo || '').toLowerCase();
    if (!primary) return;
    setCcChecked(prev => {
      if (!prev.has(primary)) return prev;
      const next = new Set(prev);
      next.delete(primary);
      return next;
    });
  }, [emailTo]);

  const toggleCc = (email) => {
    setCcChecked(prev => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  };

  const [selectedIds, setSelectedIds] = useState(() => new Set(items.map(i => i.id)));
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const toggle = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };

  // Group items by section for the preview (matches the email layout)
  const grouped = useMemo(() => {
    const bySection = {};
    items.filter(i => selectedIds.has(i.id)).forEach(i => {
      if (!bySection[i.section]) bySection[i.section] = [];
      bySection[i.section].push(i);
    });
    return Object.entries(bySection);
  }, [items, selectedIds]);

  const handleSend = async () => {
    if (!emailTo.trim()) { showToast?.('Recipient email required', 'error'); return; }
    if (selectedIds.size === 0) { showToast?.('Select at least one item', 'error'); return; }

    setSending(true);
    try {
      // 1. Generate a scoped portal token
      const tokenResult = scope === 'seller'
        ? await generateSellerToken(dealId)
        : await generateScopedSellerToken(dealId, scope, emailTo.trim(), contact?.name || meta.label);
      const token = Array.isArray(tokenResult) ? tokenResult[0].token : tokenResult.token;
      const portalUrl = token ? buildSellerPortalUrl(token) : '';

      // 2. Flip each selected item to 'requested'
      await Promise.all([...selectedIds].map(id => updateItem(id, { status: 'requested' })));

      // 3. Build the email HTML
      const recipientName = contact?.name || meta.label;
      const sectionsHtml = grouped.map(([section, sectionItems]) => `
        <p style="margin: 15px 0 5px 0; font-weight: bold; color: #1A3665;">${escapeHtml(section)}:</p>
        <ul style="margin: 0; padding-left: 20px;">
          ${sectionItems.map(i => `<li style="margin-bottom: 5px;">${escapeHtml(i.request_text)}</li>`).join('')}
        </ul>
      `).join('');

      const buttonHtml = portalUrl ? `
        <p style="margin-top: 20px;">Please use the button below to upload these documents through your secure portal:</p>
        <div style="text-align: center; margin: 30px 0;">
          <a href="${portalUrl}"
             style="display: inline-block; background-color: #1A3665; color: white; padding: 14px 28px; text-decoration: none; border-radius: 6px; font-weight: bold; font-size: 16px;">
            Open Portal
          </a>
        </div>
        <p style="text-align: center; color: #707578; font-size: 12px;">
          Or copy this link: <a href="${portalUrl}" style="color: #1A3665;">${portalUrl}</a>
        </p>
      ` : '';

      const html = `
        <h2 style="color: #1A3665; margin-bottom: 20px;">Due Diligence Request</h2>
        <p>Dear ${escapeHtml(recipientName)},</p>
        <p>${meta.phrase} <strong>${escapeHtml(dealName || 'this transaction')}</strong>, we're requesting the following items:</p>
        <div style="background-color: #f8f9fa; border-left: 4px solid #1A3665; padding: 15px; margin: 20px 0;">
          ${sectionsHtml}
        </div>
        ${buttonHtml}
      `.trim();

      // Build the final CC list: checked candidates + any free-form
      // addresses typed into the extra-cc input. The edge function
      // dedupes against the To address and drops empties.
      const extraCcs = extraCcInput.split(/[,;\s]+/).map(s => s.trim()).filter(s => s.includes('@'));
      const ccList = Array.from(new Set([...ccChecked, ...extraCcs])).filter(e => e && e !== emailTo.trim().toLowerCase());

      const result = await sendEmail({
        to: emailTo.trim(),
        cc: ccList.length > 0 ? ccList : undefined,
        subject: `Due Diligence Request — ${dealName || 'Deal'}`,
        text: `Dear ${recipientName},\n\n${meta.phrase} ${dealName || 'this transaction'}, we're requesting the following items:\n\n`
          + grouped.map(([section, sectionItems]) =>
              section + ':\n' + sectionItems.map(i => '  • ' + i.request_text).join('\n')
            ).join('\n\n')
          + (portalUrl ? `\n\nUpload portal: ${portalUrl}` : ''),
        html,
        replyTo: userProfile?.email,
      });
      if (!result.success) throw new Error(result.error || 'Failed to send email');

      setSent(true);
      const ccSummary = ccList.length > 0 ? ` (cc ${ccList.length})` : '';
      setTimeout(() => { showToast?.(`DD request sent to ${emailTo.trim()}${ccSummary}`); onClose(); }, 1200);
    } catch (err) {
      showToast?.('Send failed: ' + err.message, 'error');
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <h3 className="font-semibold text-base" style={{ color: primaryColor }}>
            Send DD request — {meta.label}
          </h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600" disabled={sending}>
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-4 overflow-y-auto flex-1 space-y-4">
          <div>
            <label className="text-xs font-semibold text-gray-600 block mb-1">
              Primary recipient (To:) — owns the upload portal token
            </label>
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <input
                type="email"
                value={emailTo}
                onChange={e => setEmailTo(e.target.value)}
                disabled={sending}
                className="flex-1 border rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1"
                placeholder="name@firm.com"
              />
            </div>
            {contact?.name && (
              <div className="text-xs text-gray-500 mt-1">Contact: {contact.name}</div>
            )}
          </div>

          {/* CC recipients — every known deal contact gets a checkbox.
              Default-checked. Free-form input below for ad-hoc addresses. */}
          <div>
            <label className="text-xs font-semibold text-gray-600 block mb-1.5">
              CC (also receives the email)
            </label>
            {ccCandidates.length === 0 ? (
              <div className="text-xs text-gray-400 italic">
                No other deal contacts on file. Add emails below if needed.
              </div>
            ) : (
              <div className="space-y-1.5">
                {ccCandidates.map(c => {
                  const isPrimary = c.email === (emailTo || '').toLowerCase();
                  const checked = ccChecked.has(c.email) && !isPrimary;
                  return (
                    <label
                      key={c.key}
                      className={`flex items-center gap-2 px-2 py-1.5 rounded text-sm cursor-pointer transition-colors ${isPrimary ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'}`}
                      style={{ border: '1px solid #E5E9F0' }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={isPrimary || sending}
                        onChange={() => toggleCc(c.email)}
                        className="rounded accent-[#1A3665]"
                      />
                      <span className="text-[10px] uppercase font-semibold tracking-wider px-1.5 py-0.5 rounded flex-shrink-0"
                        style={{ backgroundColor: '#EFF4FB', color: '#1A3665', letterSpacing: '0.06em' }}>
                        {c.label}
                      </span>
                      <span className="font-medium truncate" style={{ color: '#0F172A' }}>
                        {c.name || c.email}
                      </span>
                      {c.name && (
                        <span className="text-xs truncate" style={{ color: '#64748B' }}>{c.email}</span>
                      )}
                      {isPrimary && (
                        <span className="ml-auto text-[10px] uppercase font-semibold" style={{ color: '#94A3B8' }}>To:</span>
                      )}
                    </label>
                  );
                })}
              </div>
            )}
            <input
              type="text"
              value={extraCcInput}
              onChange={e => setExtraCcInput(e.target.value)}
              disabled={sending}
              className="w-full mt-2 border rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1"
              placeholder="Add more emails (comma-separated)…"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-gray-600">Items to request ({selectedIds.size} of {items.length})</label>
              <div className="flex items-center gap-2 text-xs">
                <button type="button" className="text-blue-600 hover:underline"
                        onClick={() => setSelectedIds(new Set(items.map(i => i.id)))}>Select all</button>
                <button type="button" className="text-gray-500 hover:underline"
                        onClick={() => setSelectedIds(new Set())}>Clear</button>
              </div>
            </div>
            <div className="border rounded divide-y max-h-72 overflow-y-auto">
              {items.length === 0 ? (
                <div className="px-3 py-4 text-sm text-gray-400 text-center">No pending items to send.</div>
              ) : items.map(i => (
                <label key={i.id} className="flex items-start gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(i.id)}
                    onChange={() => toggle(i.id)}
                    disabled={sending}
                    className="mt-0.5 accent-blue-600"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-gray-400">{i.section} · {i.item_number}</div>
                    <div className="text-sm text-gray-700">{i.request_text}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="px-5 py-3 border-t flex justify-end gap-2">
          <button type="button" onClick={onClose} disabled={sending}
                  className="px-3 py-1.5 text-sm border rounded hover:bg-gray-50 disabled:opacity-50">
            Cancel
          </button>
          <button type="button" onClick={handleSend}
                  disabled={sending || sent || selectedIds.size === 0 || !emailTo.trim()}
                  className="px-3 py-1.5 text-sm text-white rounded hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center gap-1.5"
                  style={{ backgroundColor: primaryColor }}>
            {sending
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : sent
                ? <Check className="w-3.5 h-3.5" />
                : <Send className="w-3.5 h-3.5" />}
            {sent ? 'Sent' : sending ? 'Sending…' : `Send (${selectedIds.size})`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// RelinkFileModal — admin-only. Pick a different DD item for a file.
// ---------------------------------------------------------------------------

function RelinkFileModal({ fileId, currentItemId, allItems, onClose, showToast }) {
  const [targetId, setTargetId] = useState('');
  const [busy, setBusy] = useState(false);

  const choices = (allItems || []).filter(i => i.id !== currentItemId);

  const handleRelink = async () => {
    if (!targetId) return;
    setBusy(true);
    try {
      await relinkDdFile(fileId, targetId);
      showToast?.('File relinked');
      onClose();
    } catch (e) {
      showToast?.('Relink failed: ' + e.message, 'error');
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <h3 className="font-semibold text-sm">Move file to a different DD item</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-5 py-4">
          <label className="text-xs font-semibold text-gray-600 block mb-1">Target DD item</label>
          <select
            value={targetId}
            onChange={e => setTargetId(e.target.value)}
            disabled={busy}
            className="w-full border rounded px-2 py-1.5 text-sm"
          >
            <option value="">— Select an item —</option>
            {choices.map(i => (
              <option key={i.id} value={i.id}>
                {i.section} · {i.item_number} — {i.request_text.slice(0, 60)}{i.request_text.length > 60 ? '…' : ''}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-2">
            Linking will flip the target item to <strong>received</strong>. The previously linked item's status is left unchanged.
          </p>
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-2">
          <button type="button" onClick={onClose} disabled={busy}
                  className="px-3 py-1.5 text-sm border rounded hover:bg-gray-50 disabled:opacity-50">Cancel</button>
          <button type="button" onClick={handleRelink} disabled={busy || !targetId}
                  className="px-3 py-1.5 text-sm text-white rounded bg-blue-600 hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1.5">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />}
            Relink
          </button>
        </div>
      </div>
    </div>
  );
}

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
